import { useState, useEffect } from "react";
import { FiX, FiCalendar } from "react-icons/fi";

const AddContainerModal = ({ isOpen, onClose, onSave, editingContainer }) => {
  const [formData, setFormData] = useState({
    bookingBLNo: "",
    date: new Date(),
    containerNumber: "",
    vesselName: "",
    origin: "",
    destination: "",
    status: "In Transit",
    containerType: "20ft Standard",
    shipper: "",
    consignee: "",
    voyageNumber: "",
    expectedArrival: null,
    deliveryBookedOn: null,
    description: "",
  });

  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (editingContainer) {
      setFormData({
        bookingBLNo: editingContainer.bookingBLNo || "",
        date: editingContainer.date ? new Date(editingContainer.date) : new Date(),
        containerNumber: editingContainer.containerNumber || "",
        vesselName: editingContainer.vesselName || "",
        origin: editingContainer.origin || "",
        destination: editingContainer.destination || "",
        status: editingContainer.status || "In Transit",
        containerType: editingContainer.containerType || "20ft Standard",
        shipper: editingContainer.shipper || "",
        consignee: editingContainer.consignee || "",
        voyageNumber: editingContainer.voyageNumber || "",
        expectedArrival: editingContainer.expectedArrival
          ? new Date(editingContainer.expectedArrival)
          : null,
        deliveryBookedOn: editingContainer.deliveryBookedOn
          ? new Date(editingContainer.deliveryBookedOn)
          : null,
        description: editingContainer.description || "",
      });
    } else {
      resetForm();
    }
  }, [editingContainer, isOpen]);

  const resetForm = () => {
    setFormData({
      bookingBLNo: "",
      date: new Date(),
      containerNumber: "",
      vesselName: "",
      origin: "",
      destination: "",
      status: "In Transit",
      containerType: "20ft Standard",
      shipper: "",
      consignee: "",
      voyageNumber: "",
      expectedArrival: null,
      deliveryBookedOn: null,
      description: "",
    });
    setErrors({});
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.bookingBLNo) newErrors.bookingBLNo = "Booking/BL No is required";
    if (!formData.containerNumber) newErrors.containerNumber = "Container number is required";
    if (!formData.date) newErrors.date = "Date is required";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    const submitData = {
      ...formData,
      bookingBLNo: formData.bookingBLNo.trim(),
      containerNumber: formData.containerNumber.trim().toUpperCase(),
      date: formData.date.toISOString().split("T")[0],
      expectedArrival: formData.expectedArrival
        ? formData.expectedArrival.toISOString().split("T")[0]
        : null,
      deliveryBookedOn: formData.deliveryBookedOn
        ? formData.deliveryBookedOn.toISOString().split("T")[0]
        : null,
    };

    await onSave(submitData);
    resetForm();
    onClose();
  };

  const statusOptions = [
    "In Transit",
    "Arrived at Port",
    "Customs Cleared",
    "Delivered",
    "Departed from Origin Port",
  ];

  const containerTypeOptions = [
    "20ft Standard",
    "40ft Standard",
    "40ft High Cube",
    "45ft High Cube",
    "Reefer 20ft",
    "Reefer 40ft",
    "Open Top",
    "Flat Rack",
  ];

  const formatDateForInput = (dateValue) => {
    if (!dateValue) return "";
    const dateObj = dateValue instanceof Date ? dateValue : new Date(dateValue);
    if (Number.isNaN(dateObj.getTime())) return "";
    return dateObj.toISOString().split("T")[0];
  };

  if (!isOpen) return null;

  const inputClass =
    "w-full rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm outline-none transition placeholder:text-slate-400 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100 dark:placeholder:text-slate-500";

  const labelClass = "mb-2 block text-sm font-medium text-slate-700 dark:text-slate-300";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-[2px]">
      <div className="max-h-[90vh] w-full max-w-4xl overflow-y-auto rounded-3xl border border-slate-200 bg-white shadow-2xl dark:border-slate-800 dark:bg-slate-900">
        <div className="sticky top-0 z-10 flex items-center justify-between border-b border-slate-100 bg-white px-6 py-4 dark:border-slate-800 dark:bg-slate-900">
          <div>
            <h2 className="text-xl font-semibold text-slate-900 dark:text-slate-100">
              {editingContainer ? "Edit Container" : "Add New Container"}
            </h2>
            <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
              Fill in the container details below.
            </p>
          </div>

          <button
            onClick={onClose}
            className="grid h-10 w-10 place-items-center rounded-full border border-slate-200 text-slate-500 transition hover:bg-slate-50 hover:text-slate-700 dark:border-slate-700 dark:text-slate-400 dark:hover:bg-slate-800 dark:hover:text-slate-200"
            aria-label="Close modal"
            type="button"
          >
            <FiX size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6">
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
            <div>
              <label className={labelClass}>Booking/BL No *</label>
              <input
                type="text"
                value={formData.bookingBLNo}
                onChange={(e) =>
                  setFormData({ ...formData, bookingBLNo: e.target.value })
                }
                className={`${inputClass} ${errors.bookingBLNo ? "border-red-500 focus:border-red-500 focus:ring-red-500/20" : ""}`}
                placeholder="Booking / BL number"
              />
              {errors.bookingBLNo && (
                <p className="mt-1 text-xs text-red-500">{errors.bookingBLNo}</p>
              )}
            </div>

            <div>
              <label className={labelClass}>Date *</label>
              <div className="relative">
                <input
                  type="date"
                  value={formatDateForInput(formData.date)}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      date: e.target.value ? new Date(e.target.value) : null,
                    })
                  }
                  className={`${inputClass} pr-10 ${errors.date ? "border-red-500 focus:border-red-500 focus:ring-red-500/20" : ""}`}
                />
                <FiCalendar className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 dark:text-slate-500" />
              </div>
              {errors.date && <p className="mt-1 text-xs text-red-500">{errors.date}</p>}
            </div>

            <div>
              <label className={labelClass}>Container Number *</label>
              <input
                type="text"
                value={formData.containerNumber}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    containerNumber: e.target.value.toUpperCase(),
                  })
                }
                className={`${inputClass} ${errors.containerNumber ? "border-red-500 focus:border-red-500 focus:ring-red-500/20" : ""}`}
                placeholder="e.g., ABCD1234567"
              />
              {errors.containerNumber && (
                <p className="mt-1 text-xs text-red-500">{errors.containerNumber}</p>
              )}
            </div>

            <div>
              <label className={labelClass}>Vessel Name</label>
              <input
                type="text"
                value={formData.vesselName}
                onChange={(e) =>
                  setFormData({ ...formData, vesselName: e.target.value })
                }
                className={inputClass}
                placeholder="Vessel name"
              />
            </div>

            <div>
              <label className={labelClass}>Origin</label>
              <input
                type="text"
                value={formData.origin}
                onChange={(e) => setFormData({ ...formData, origin: e.target.value })}
                className={inputClass}
                placeholder="Origin"
              />
            </div>

            <div>
              <label className={labelClass}>Destination</label>
              <input
                type="text"
                value={formData.destination}
                onChange={(e) =>
                  setFormData({ ...formData, destination: e.target.value })
                }
                className={inputClass}
                placeholder="Destination"
              />
            </div>

            <div>
              <label className={labelClass}>Status</label>
              <select
                value={formData.status}
                onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                className={inputClass}
              >
                {statusOptions.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className={labelClass}>Container Type</label>
              <select
                value={formData.containerType}
                onChange={(e) =>
                  setFormData({ ...formData, containerType: e.target.value })
                }
                className={inputClass}
              >
                {containerTypeOptions.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className={labelClass}>Shipper</label>
              <input
                type="text"
                value={formData.shipper}
                onChange={(e) => setFormData({ ...formData, shipper: e.target.value })}
                className={inputClass}
                placeholder="Shipper name"
              />
            </div>

            <div>
              <label className={labelClass}>Consignee</label>
              <input
                type="text"
                value={formData.consignee}
                onChange={(e) =>
                  setFormData({ ...formData, consignee: e.target.value })
                }
                className={inputClass}
                placeholder="Consignee name"
              />
            </div>

            <div>
              <label className={labelClass}>Voyage Number</label>
              <input
                type="text"
                value={formData.voyageNumber}
                onChange={(e) =>
                  setFormData({ ...formData, voyageNumber: e.target.value })
                }
                className={inputClass}
                placeholder="Voyage number"
              />
            </div>

            <div>
              <label className={labelClass}>Expected Arrival</label>
              <div className="relative">
                <input
                  type="date"
                  value={formatDateForInput(formData.expectedArrival)}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      expectedArrival: e.target.value ? new Date(e.target.value) : null,
                    })
                  }
                  className={`${inputClass} pr-10`}
                />
                <FiCalendar className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 dark:text-slate-500" />
              </div>
            </div>

            <div className="col-span-1 md:col-span-2">
              <label className={labelClass}>Description</label>
              <textarea
                value={formData.description}
                onChange={(e) =>
                  setFormData({ ...formData, description: e.target.value })
                }
                className={inputClass}
                rows="3"
                placeholder="Additional details about the container..."
              />
            </div>

            <div className="col-span-1 md:col-span-2">
              <label className={labelClass}>Delivery Booked On</label>
              <div className="relative">
                <input
                  type="date"
                  value={formatDateForInput(formData.deliveryBookedOn)}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      deliveryBookedOn: e.target.value ? new Date(e.target.value) : null,
                    })
                  }
                  className={`${inputClass} pr-10`}
                />
                <FiCalendar className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 dark:text-slate-500" />
              </div>
            </div>
          </div>

          <div className="mt-6 flex justify-end gap-3 border-t border-slate-100 pt-4 dark:border-slate-800">
            <button
              type="button"
              onClick={onClose}
              className="rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-medium text-slate-700 transition hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-300 dark:hover:bg-slate-800"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="rounded-xl bg-blue-600 px-4 py-2.5 text-sm font-medium text-white transition hover:bg-blue-700"
            >
              {editingContainer ? "Update Container" : "Add Container"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddContainerModal;